import React from "react";
import { ArrowRight } from "lucide-react";
import { BOOK_A_CALL_URL } from "@/constants/site";
import { cn } from "@/lib/utils";

/**
 * BookACallButton — the ONLY conversion CTA on the site.
 * Always points to https://grow.theelevara.com (opens in new tab).
 */
export function BookACallButton({
    children = "Book a Call",
    variant = "primary",
    size = "md",
    className = "",
    showArrow = true,
    pulse = false,
    shimmer = true,
    testId = "book-a-call-button",
    ...rest
}) {
    const base =
        "group inline-flex items-center justify-center gap-2 font-display font-bold tracking-tight rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(24,139,246,0.55)] focus-visible:ring-offset-2 focus-visible:ring-offset-white active:scale-[0.98] whitespace-nowrap will-change-transform";

    const variants = {
        primary:
            "bg-[#188bf6] text-white shadow-[0_6px_18px_-8px_rgba(24,139,246,0.55)] hover:bg-[#0b72d8] hover:shadow-[0_10px_26px_-8px_rgba(24,139,246,0.75)] hover:-translate-y-0.5",
        ghost:
            "bg-white text-[#0a0b0d] border-2 border-[#0a0b0d]/15 hover:border-[#188bf6] hover:text-[#0b72d8] hover:bg-[#188bf6]/5",
        inverse:
            "bg-[#0a0b0d] text-white shadow-[0_6px_18px_-8px_rgba(10,11,13,0.45)] hover:bg-[#188bf6] hover:-translate-y-0.5",
    };

    const sizes = {
        sm: "h-9 sm:h-10 px-3.5 sm:px-4 text-[12px] sm:text-[13px]",
        md: "h-11 sm:h-12 px-4 sm:px-5 text-sm",
        lg: "h-12 sm:h-14 lg:h-16 px-5 sm:px-7 text-sm sm:text-base lg:text-[17px]",
    };

    return (
        <a
            href={BOOK_A_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={testId}
            className={cn(
                base,
                variants[variant],
                sizes[size],
                shimmer ? "cta-shimmer" : "",
                pulse ? "animate-cta-pulse-slow" : "",
                className,
            )}
            {...rest}
        >
            <span className="relative z-10">{children}</span>
            {showArrow ? (
                <ArrowRight
                    className={cn(
                        "relative z-10 transition-transform duration-200 group-hover:translate-x-1",
                        size === "lg"
                            ? "w-4 h-4 sm:w-5 sm:h-5"
                            : "w-3.5 h-3.5 sm:w-4 sm:h-4",
                    )}
                    aria-hidden="true"
                />
            ) : null}
        </a>
    );
}

export default BookACallButton;
